import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResultCard } from "@/components/calculator/ResultCard";
import { PresetButton } from "@/components/calculator/PresetButton";
import { PowerTriangle } from "@/components/calculator/PowerTriangle";
import { Fan, Heater, Home } from "lucide-react";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

export const SinglePhaseTab = ({ onSaveHistory }) => {
  const [voltage, setVoltage] = useState(240);
  const [current, setCurrent] = useState(20);
  const [powerFactor, setPowerFactor] = useState(0.85);
  const [powerUnit, setPowerUnit] = useState("auto");
  
  const debouncedVoltage = useDebounce(voltage, 200);
  const debouncedCurrent = useDebounce(current, 200);
  const debouncedPF = useDebounce(powerFactor, 200);

  // Calculations (1Φ AC formulas)
  const apparentPower = debouncedVoltage * debouncedCurrent; // S = V × I (VA)
  const realPower = apparentPower * debouncedPF; // P = V × I × PF (W)
  const reactivePower = Math.sqrt(Math.pow(apparentPower, 2) - Math.pow(realPower, 2)); // Q = √(S² - P²) (VAR)

  // Auto unit formatting
  const formatPower = useCallback((watts, type = "real") => {
    const useKilo = powerUnit === "kilo" || (powerUnit === "auto" && watts >= 1000);
    const value = useKilo ? watts / 1000 : watts;
    const decimals = useKilo ? 3 : 2;
    
    let unit;
    switch (type) {
      case "apparent":
        unit = useKilo ? "kVA" : "VA";
        break;
      case "reactive":
        unit = useKilo ? "kVAR" : "VAR";
        break;
      default:
        unit = useKilo ? "kW" : "W";
    }
    
    return { value: value.toFixed(decimals), unit };
  }, [powerUnit]);

  // Save to history
  const handleSave = useCallback(() => {
    const real = formatPower(realPower, "real");
    const apparent = formatPower(apparentPower, "apparent");
    const reactive = formatPower(reactivePower, "reactive");
    
    onSaveHistory({
      type: "Single-Phase AC",
      inputs: {
        voltage: `${debouncedVoltage} V`,
        current: `${debouncedCurrent} A`,
        powerFactor: debouncedPF.toFixed(2)
      },
      outputs: {
        realPower: `${real.value} ${real.unit}`,
        apparentPower: `${apparent.value} ${apparent.unit}`,
        reactivePower: `${reactive.value} ${reactive.unit}`
      }
    });
  }, [debouncedVoltage, debouncedCurrent, debouncedPF, realPower, apparentPower, reactivePower, formatPower, onSaveHistory]);

  // Presets
  const presets = [
    { name: "HVAC Unit", voltage: 240, current: 35, pf: 0.85, icon: Fan },
    { name: "Space Heater", voltage: 120, current: 12, pf: 1.0, icon: Heater },
    { name: "Residential Load", voltage: 240, current: 100, pf: 0.9, icon: Home },
  ];

  const applyPreset = (preset) => {
    setVoltage(preset.voltage);
    setCurrent(preset.current);
    setPowerFactor(preset.pf);
  };

  const real = formatPower(realPower, "real");
  const apparent = formatPower(apparentPower, "apparent");
  const reactive = formatPower(reactivePower, "reactive");

  return (
    <div className="space-y-6">
      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          <PresetButton
            key={preset.name}
            label={preset.name}
            icon={preset.icon}
            onClick={() => applyPreset(preset)}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-6">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Input Parameters
          </h3>

          {/* Voltage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="single-voltage" className="text-sm font-medium">
                Voltage (V)
              </Label>
              <div className="flex gap-1">
                {[120, 240, 480, 600].map((v) => (
                  <Button
                    key={v}
                    variant={voltage === v ? "default" : "outline"}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setVoltage(v)}
                  >
                    {v}V
                  </Button>
                ))}
              </div>
            </div>
            <Input
              id="single-voltage"
              type="number"
              min="0"
              max="1000"
              value={voltage}
              onChange={(e) => setVoltage(Number(e.target.value) || 0)}
              className="bg-secondary/50 border-border focus:border-primary"
            />
          </div>

          {/* Current */}
          <div className="space-y-2">
            <Label htmlFor="single-current" className="text-sm font-medium">
              Current (A)
            </Label>
            <Input
              id="single-current"
              type="number"
              min="0"
              max="10000"
              value={current}
              onChange={(e) => setCurrent(Number(e.target.value) || 0)}
              className="bg-secondary/50 border-border focus:border-primary"
            />
          </div>

          {/* Power Factor */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Power Factor (PF)
              </Label>
              <span className="text-sm font-mono text-primary">{powerFactor.toFixed(2)}</span>
            </div>
            <Slider
              value={[powerFactor * 100]}
              onValueChange={(value) => setPowerFactor(value[0] / 100)}
              min={20}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0.20 (Lagging)</span>
              <span>1.00 (Unity)</span>
            </div>
            {powerFactor < 0.85 && (
              <p className="text-xs text-warning bg-warning/10 p-2 rounded">
                ⚠️ PF below 0.85 may incur utility penalties
              </p>
            )}
          </div>

          {/* Unit Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Power Display Unit</Label>
            <Select value={powerUnit} onValueChange={setPowerUnit}>
              <SelectTrigger className="bg-secondary/50 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (W/kW)</SelectItem>
                <SelectItem value="base">Base (W, VA, VAR)</SelectItem>
                <SelectItem value="kilo">Kilo (kW, kVA, kVAR)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Results
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              className="text-xs"
            >
              Save to History
            </Button>
          </div>

          <div className="space-y-4">
            <ResultCard
              label="Real Power (P)"
              value={real.value}
              unit={real.unit}
              color="real"
              formula="P = V × I × PF"
            />
            
            <ResultCard
              label="Apparent Power (S)"
              value={apparent.value}
              unit={apparent.unit}
              color="apparent"
              formula="S = V × I"
            />
            
            <ResultCard
              label="Reactive Power (Q)"
              value={reactive.value}
              unit={reactive.unit}
              color="reactive"
              formula="Q = √(S² - P²)"
            />

            <ResultCard
              label="Power Factor"
              value={debouncedPF.toFixed(2)}
              unit=""
              color="muted"
              formula="PF = P / S"
            />
          </div>

          {/* Power Triangle */}
          <PowerTriangle
            realPower={realPower}
            reactivePower={reactivePower}
            apparentPower={apparentPower}
            powerFactor={debouncedPF}
          />
        </div>
      </div>
    </div>
  );
};
